import { useCallback, useMemo, useRef, useState } from 'react';
import { AdditionalInfoView, Priority } from '../../types';
import { useAdditionalInfos } from '../../hooks/use-additional-infos/additional-infos-provider';
import { usePinNarrative } from '../../hooks/use-pin-narrative/use-pin-narrative';
import { usePinCap } from '../../hooks/use-pin-narrative/use-pin-cap';

/**
 * Row hover/selection contract. Every narrative row
 * (`AdditionalInfoRow`, `PinnedNarrativeRow`) follows these 5 rules; each
 * lists where it's enforced so a change in one place doesn't quietly break
 * another. Covered by `use-narrative-row-actions.test.ts`'s
 * "Row hover/select rules" block (rules 4-5, and the JS-side gate
 * behind rules 1-3) and `narrative-action-menu.component-test.tsx`'s same-named
 * block (rules 1-3's real hover/visibility behavior, which needs a real
 * browser to verify).
 *
 *   1. A selected row shows its action menu — except while ANY row (not just
 *      this one) is editing/deleting.
 *      Enforced by: `canShowActionMenu` below (gates whether
 *      `<NarrativeActionMenu>` mounts at all) + CSS
 *      `.row.is-selected :global(.narrative-action-menu) { display: flex }`
 *      in additional-info-row.module.css (`.pinned-row` equivalent in
 *      pinned-narrative-row.module.css).
 *   2. A hovered row shows its action menu — same editing/deleting exception
 *      as rule 1.
 *      Enforced by: the same `canShowActionMenu` gate + CSS
 *      `.row:hover :global(.narrative-action-menu) { display: flex }`. Hover
 *      is plain CSS `:hover` — there is no JS "isHovered" state to keep in
 *      sync.
 *   3. The action menu is never visible unless the row is selected or
 *      hovered.
 *      Enforced by: those two CSS selectors being the ONLY rules in either
 *      module.css file that set `.narrative-action-menu`'s `display`. Do not
 *      add a third (e.g. `:focus-within`, `.is-editing`) without updating
 *      this contract.
 *   4. Clicking any action-menu button with a real mouse/touch click clears
 *      the row's selection; activating a button via keyboard (Enter/Space)
 *      performs the action but leaves selection untouched, since selection
 *      itself is a keyboard-only concept (see rule 5).
 *      Enforced by: `handleRowDeselect` below, wired as
 *      `NarrativeActionMenu`'s `onActionClick` — invoked by that component's
 *      `handleMenuClick` for a click landing on (or inside) any of its
 *      buttons, gated on `event.detail !== 0` to exclude keyboard-synthesized
 *      clicks.
 *   5. Selection can only be SET by keyboard navigation, never by a mouse
 *      click; a click instead clears whatever was selected — regardless of
 *      which row is currently selected, not just this one.
 *      Enforced by: `handleRowMouseDown` + `handleRowFocus` below —
 *      mouse-driven focus is flagged on mousedown and `handleRowFocus` skips
 *      `setSelectedRow` for it — plus `handleRowDeselect`, which calls the
 *      store's unconditional `clearSelectedRow()` (not the id-scoped
 *      `clearSelectedRowIfMatches` that `handleRowBlur` uses). Relying on
 *      `handleRowBlur` alone to clear a *different*, previously-selected row
 *      is NOT reliable: it requires that row's DOM node to still hold real
 *      focus at click time, which can silently stop being true (e.g. an
 *      unrelated re-render replaces its node while scrolled off-screen,
 *      the same shape the virtualizer-survival case in handleRowBlur is
 *      deliberately built to ignore) — once focus and `selectedRow` drift
 *      apart like that, no further blur ever fires to reconcile them. A
 *      plain click's unconditional clear needs no such assumption.
 */

/**
 * Options that let each row parameterize the genuine divergences between
 * `AdditionalInfoRow` and `PinnedNarrativeRow`:
 *   1. `isPinnedRow` — which of the two instances this is. A pinned entry and
 *      its chronological twin share `info.id`; the hook derives edit /
 *      delete-confirmation from the store's single active-row action scoped by
 *      (id + row kind), so only the acting instance lights up.
 *   2. `shouldConfirmBeforeDelete` — whether delete opens the shared
 *      confirmation first, or deletes immediately (the legacy flag-off trash
 *      affordance on the chronological row).
 *   3. what happens after a delete succeeds / fails (`onDeleteSuccess` /
 *      `onDeleteError`).
 * Everything else (edit state, emphasize toggle + ref-guard, pin/unpin
 * passthrough, the in-flight delete state) is byte-identical and owned here.
 */
export interface UseNarrativeRowActionsOptions {
    /**
     * The row's info entity. `agencyEventId` is the event this narrative is
     * actually linked to (which for a merged family may differ from the panel's
     * entity) and scopes pin/unpin/delete to the correct link.
     */
    info: Pick<AdditionalInfoView, 'id' | 'priority' | 'agencyEventId'>;
    /**
     * Which of the two row instances this is: `true` for the pinned-section
     * row, `false` for the chronological-list row. Disambiguates the two rows
     * that share `info.id` so only the acting one shows the editor /
     * delete-confirmation.
     */
    isPinnedRow: boolean;
    /** Returns real DOM focus to this row after an edit-cancel, edit-save, or
     *  delete-cancel — which also naturally re-selects it (handleRowFocus
     *  selects on any focus not preceded by a click). Only the delete-
     *  CONFIRMED path is different: the row is gone, so its caller uses
     *  its own focusNarrativeInput instead (see onDeleteSuccess). */
    returnFocusToRow?: () => void;
    /**
     * Whether clicking delete opens the shared confirmation first (flag-on rows
     * and all pinned rows) or deletes immediately (the legacy flag-off trash
     * affordance on the chronological row — pinned rows only exist when the flag
     * is on, so they always confirm).
     */
    shouldConfirmBeforeDelete: boolean;
    /**
     * Extra side effects after `deleteInfo` resolves successfully. The hook
     * already clears the active-row action; callers add only what diverges:
     *   - AdditionalInfoRow: toast + focus, but ONLY when the narrative-filtering
     *     flag is on.
     *   - PinnedNarrativeRow: always toast + focus.
     */
    onDeleteSuccess?: () => void;
    /**
     * Runs in the delete `catch` block. Each caller supplies its own error
     * surface (inline toast vs. showFailedToDeleteAlert).
     */
    onDeleteError?: () => void;
}

export interface UseNarrativeRowActionsResult {
    // edit
    isEditing: boolean;
    isSaving: boolean;
    handleEdit: () => void;
    /** `isKeyboardActivated` (derived from the triggering click's
     *  `event.detail === 0`) decides stay-selected vs deselect: true returns
     *  real DOM focus to the row (re-selecting it — a deliberate "stay put"
     *  for keyboard use); false deselects instead, matching a plain mouse
     *  click anywhere on the row. See the module doc's note on
     *  handleEditCancel/handleEditSave for why this can't just piggyback on
     *  handleRowDeselect's own click-bubble gating. */
    handleEditCancel: (isKeyboardActivated: boolean) => void;
    handleEditSave: (
        updatedInfo: AdditionalInfoView,
        isKeyboardActivated: boolean
    ) => Promise<void>;
    // emphasize
    isTogglingEmphasis: boolean;
    handleToggleEmphasis: () => Promise<void>;
    // delete
    isDeleting: boolean;
    isDeleteConfirmOpen: boolean;
    executeDelete: () => Promise<void>;
    handleDelete: () => void;
    /** Same `isKeyboardActivated` contract as handleEditCancel above. */
    handleDeleteCancel: (isKeyboardActivated: boolean) => void;
    // pin / unpin passthrough
    handlePin: () => Promise<void>;
    handleUnpin: () => Promise<void>;
    isPinning: boolean;
    // pin cap
    isPinCapReached: boolean;
    pinCapTooltip: string | undefined;
    // row selection / action-menu visibility
    /** False while another row is editing/deleting — so hovering this row
     *  can't reveal an action menu that would act on an entry mid-edit or
     *  mid-delete elsewhere. A row simply being *selected* (not editing/
     *  deleting) does NOT block other rows' menus — selection alone is a
     *  passive, non-destructive state, so hovering around while one row is
     *  selected is still safe. */
    canShowActionMenu: boolean;
    /** Whether this row is the app-level selected row. Drives a CSS class
     *  (not just :focus-within) so the selected look survives the row's DOM
     *  node being unmounted and remounted by the virtualizer — e.g. scrolling
     *  a selected row out of view and back leaves it looking selected even
     *  though real DOM focus never returns to it on its own. */
    isSelected: boolean;
    /** Wire onto the row's onFocus (in addition to any keyboard-nav onFocus).
     *  Only selects the row when the incoming focus was not click-driven —
     *  see handleRowMouseDown. Clicking a row moves DOM focus but must not
     *  mark it selected; only keyboard navigation (arrow keys, Tab) does. */
    handleRowFocus: () => void;
    /** Wire onto the row's onBlur. */
    handleRowBlur: (event: { relatedTarget: EventTarget | null }) => void;
    /** Unconditionally clears WHATEVER row is currently selected — not
     *  scoped to this row's own id. Wire directly onto the row's onClick —
     *  clicking a row never selects it (see handleRowFocus), and per rule 5
     *  a click always clears the selected row if any, regardless of which
     *  row that is. Also reused by NarrativeActionMenu's onActionClick (any
     *  menu action clears selection) and the Escape handler. */
    handleRowDeselect: () => void;
    /** Wire onto the row's onMouseDown. Does two things together, since
     *  they're always needed together: (1) preventDefault()s the browser's
     *  click-to-focus action while a DIFFERENT row is editing/deleting, so
     *  clicking around the list can't steal real DOM focus away from the row
     *  being acted on (its open inline editor or delete confirmation); (2)
     *  marks the focus about to land on THIS row as click-driven, so the
     *  following handleRowFocus skips selecting it. */
    handleRowMouseDown: (event: { preventDefault: () => void }) => void;
}

/**
 * Shared per-row action logic for the two narrative rows. Extracts the
 * ~90 lines of byte-identical action-handler wiring (edit, emphasize, delete,
 * pin/unpin) that previously lived in both `additional-info-row.tsx` and
 * `pinned-narrative-row.tsx`.
 *
 * Edit and delete-confirmation both derive from the store's single
 * `activeRowAction` ({ id, kind, isPinnedRow }). A row is "acting" only when the
 * action's id AND row kind match this instance — so pinning a narrative and
 * editing/deleting it no longer lights up both the pinned row and its
 * chronological twin.
 *
 * Returns a memoized bag of stable references so consumers can safely wire any
 * value into a downstream `useMemo`/`useEffect` dependency array.
 */
export function useNarrativeRowActions({
    info,
    isPinnedRow,
    returnFocusToRow,
    shouldConfirmBeforeDelete,
    onDeleteSuccess,
    onDeleteError,
}: UseNarrativeRowActionsOptions): UseNarrativeRowActionsResult {
    const {
        deleteInfo,
        editInfo,
        emphasizeInfo,
        removeEmphasis,
        activeRowAction,
        setActiveRowAction,
        clearActiveRowAction,
        selectedRow,
        setSelectedRow,
        clearSelectedRow,
        clearSelectedRowIfMatches,
    } = useAdditionalInfos();
    const { handlePin, handleUnpin, isPinning } = usePinNarrative(info.id, info.agencyEventId);
    const { isPinCapReached, pinCapTooltip } = usePinCap();

    // A pinned entry and its chronological twin share info.id, so match on BOTH
    // id and row kind — only the acting instance is live.
    const isActionOnThisRow =
        activeRowAction?.id === info.id && activeRowAction?.isPinnedRow === isPinnedRow;
    const isEditing = isActionOnThisRow && activeRowAction?.kind === 'edit';
    const isDeleteConfirmOpen = isActionOnThisRow && activeRowAction?.kind === 'delete-confirm';

    const isThisRowSelected =
        selectedRow !== null &&
        selectedRow.id === info.id &&
        selectedRow.isPinnedRow === isPinnedRow;
    // Only an in-flight edit/delete blocks other rows' menus — a plain
    // selection (no edit/delete) does not.
    const canShowActionMenu = activeRowAction === null;

    const isSelectionBlockedByOtherRowAction = activeRowAction !== null && !isActionOnThisRow;

    // Set synchronously on mousedown. The browser's default focus-on-mousedown
    // action (moving real DOM focus to the row) runs only after mousedown's
    // own listeners finish — same reason preventDefault() in a mousedown
    // handler can still cancel that focus move (see handleRowMouseDown below)
    // — so by the time the resulting onFocus fires, this ref already
    // reflects whether THIS focus was click-driven. Reset on both focus (once
    // read) and blur, so a mousedown that never produces a new focus event
    // (e.g. clicking a row that already had focus) can't leave it stuck true
    // for a later, unrelated keyboard focus.
    const wasMouseFocusRef = useRef(false);

    const handleRowFocus = useCallback(() => {
        const wasMouseFocus = wasMouseFocusRef.current;
        wasMouseFocusRef.current = false;
        // The scheduled event's synthetic initial narrative has no id.
        if (info.id === undefined) return;
        if (isSelectionBlockedByOtherRowAction) return;
        if (wasMouseFocus) return;
        setSelectedRow({ id: info.id, isPinnedRow });
    }, [info.id, isPinnedRow, setSelectedRow, isSelectionBlockedByOtherRowAction]);

    // relatedTarget is the element about to receive focus, if any. It's null
    // when focus reverts to <body> with nothing claiming it — this row's DOM
    // node removed by the virtualizer while scrolled out of view — which must
    // NOT deselect, so scrolling back finds it still selected. Deliberate
    // deselects (a click, or a menu action) go through handleRowDeselect
    // instead, which clears unconditionally regardless of what relatedTarget
    // is. So this only ever deselects on a real move to another focusable
    // element (another row, the narrative input, …).
    const handleRowBlur = useCallback(
        (event: { relatedTarget: EventTarget | null }) => {
            wasMouseFocusRef.current = false;
            if (info.id === undefined) return;
            if (event.relatedTarget === null) return;
            clearSelectedRowIfMatches({ id: info.id, isPinnedRow });
        },
        [info.id, isPinnedRow, clearSelectedRowIfMatches]
    );

    // The general-purpose "clear whatever row is selected" primitive —
    // unconditional (store.clearSelectedRow, not the id-scoped
    // clearSelectedRowIfMatches that handleRowBlur uses), not inferred from a
    // blur event, so it works the same regardless of caller: the row's own
    // onClick (a click never selects, and clicking an already-selected row
    // must deselect it the same way clicking any other row would — see
    // additional-info-row.tsx), NarrativeActionMenu's onActionClick (any menu
    // action clears selection), and the Escape handler (row or menu). Being
    // unconditional also means a click reliably clears a DIFFERENT,
    // previously-selected row even if that row's own blur never fires — see
    // this hook's module doc, rule 5.
    const handleRowDeselect = useCallback(() => {
        clearSelectedRow();
    }, [clearSelectedRow]);

    // Two things that always happen together on mousedown, so this is one
    // function rather than two: (1) preventDefault()ing the browser's
    // click-to-focus default action stops real DOM focus from ever leaving
    // the acting row in the first place — e.g. its open inline editor or
    // delete confirmation — while a click elsewhere in the list is in
    // flight. Selection itself doesn't need this: handleRowFocus's own
    // isSelectionBlockedByOtherRowAction check already stops a different row
    // from becoming selected regardless of where real focus lands. (2)
    // flagging the about-to-land focus as click-driven, consumed by
    // handleRowFocus above.
    const handleRowMouseDown = useCallback(
        (event: { preventDefault: () => void }) => {
            if (isSelectionBlockedByOtherRowAction) {
                event.preventDefault();
            }
            wasMouseFocusRef.current = true;
        },
        [isSelectionBlockedByOtherRowAction]
    );

    const [isSaving, setIsSaving] = useState(false);
    const [isTogglingEmphasis, setIsTogglingEmphasis] = useState(false);
    const isTogglingEmphasisRef = useRef(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleEdit = useCallback(
        () => setActiveRowAction({ id: info.id, kind: 'edit', isPinnedRow }),
        [info.id, isPinnedRow, setActiveRowAction]
    );

    // Cancelling or saving an edit via KEYBOARD returns focus to the row
    // itself rather than the compose input — a deliberate "stay put" action,
    // not a "done with this row" one. Returning real DOM focus also naturally
    // re-selects the row (handleRowFocus selects on any focus not preceded
    // by a click), so no separate handleRowDeselect + refocus-elsewhere is
    // needed for that path. A real mouse/touch click instead deselects
    // outright, via the same explicit handleRowDeselect call handleDelete's
    // menu buttons use for a mouse click — clicking anywhere, including
    // Cancel/Save, clears selection; only keyboard activation keeps the row
    // "selected" as a navigation aid. Both branches are explicit here rather
    // than relying on click-bubble timing, since handleEditSave's async gap
    // makes "let it bubble to the row's onClick" an unreliable way to decide
    // mouse-vs-keyboard outcome for a Promise-returning handler.
    const handleEditCancel = useCallback(
        (isKeyboardActivated: boolean) => {
            clearActiveRowAction();
            if (isKeyboardActivated) {
                returnFocusToRow?.();
            } else {
                handleRowDeselect();
            }
        },
        [clearActiveRowAction, returnFocusToRow, handleRowDeselect]
    );

    const handleEditSave = useCallback(
        async (updatedInfo: AdditionalInfoView, isKeyboardActivated: boolean) => {
            try {
                setIsSaving(true);
                await editInfo({ additionalInfoId: info.id, text: updatedInfo.text });
                clearActiveRowAction();
                if (isKeyboardActivated) {
                    returnFocusToRow?.();
                } else {
                    handleRowDeselect();
                }
            } catch {
                // edit failed — keep edit mode open so user can retry
            } finally {
                setIsSaving(false);
            }
        },
        [editInfo, info.id, clearActiveRowAction, returnFocusToRow, handleRowDeselect]
    );

    const handleToggleEmphasis = useCallback(async () => {
        if (isTogglingEmphasisRef.current) {
            return;
        }
        try {
            isTogglingEmphasisRef.current = true;
            setIsTogglingEmphasis(true);
            await (info.priority === Priority.HIGH
                ? removeEmphasis(info.id)
                : emphasizeInfo(info.id));
        } catch {
            // toggle failed — silently ignore, matching the text-edit path
        } finally {
            isTogglingEmphasisRef.current = false;
            setIsTogglingEmphasis(false);
        }
    }, [info.priority, info.id, emphasizeInfo, removeEmphasis]);

    const executeDelete = useCallback(async () => {
        try {
            setIsDeleting(true);
            await deleteInfo(info.id, info.agencyEventId);
            // Close the confirmation (no-op on the legacy direct-delete path,
            // where no active action was ever set) before caller side effects.
            clearActiveRowAction();
            handleRowDeselect();
            onDeleteSuccess?.();
        } catch {
            // Leave the confirmation open on failure so the user can retry.
            onDeleteError?.();
        } finally {
            setIsDeleting(false);
        }
    }, [
        deleteInfo,
        info.id,
        info.agencyEventId,
        clearActiveRowAction,
        handleRowDeselect,
        onDeleteSuccess,
        onDeleteError,
    ]);

    const handleDelete = useCallback(() => {
        if (shouldConfirmBeforeDelete) {
            setActiveRowAction({ id: info.id, kind: 'delete-confirm', isPinnedRow });
        } else {
            executeDelete();
        }
    }, [shouldConfirmBeforeDelete, info.id, isPinnedRow, setActiveRowAction, executeDelete]);

    // Same isKeyboardActivated contract as handleEditCancel/handleEditSave
    // above. Unlike those two, the confirmation UI fully replaces the row
    // (see additional-info-row.tsx's isDeleteConfirmOpen early return) rather
    // than nesting inside it, so there's no click-bubble path to the row's
    // onClick at all here regardless of modality — the mouse branch must
    // deselect explicitly, it can't rely on an ambient bubble to do it.
    const handleDeleteCancel = useCallback(
        (isKeyboardActivated: boolean) => {
            clearActiveRowAction();
            if (isKeyboardActivated) {
                returnFocusToRow?.();
            } else {
                handleRowDeselect();
            }
        },
        [clearActiveRowAction, returnFocusToRow, handleRowDeselect]
    );

    return useMemo(
        () => ({
            isEditing,
            isSaving,
            handleEdit,
            handleEditCancel,
            handleEditSave,
            isTogglingEmphasis,
            handleToggleEmphasis,
            isDeleting,
            isDeleteConfirmOpen,
            executeDelete,
            handleDelete,
            handleDeleteCancel,
            handlePin,
            handleUnpin,
            isPinning,
            isPinCapReached,
            pinCapTooltip,
            canShowActionMenu,
            isSelected: isThisRowSelected,
            handleRowFocus,
            handleRowBlur,
            handleRowDeselect,
            handleRowMouseDown,
        }),
        [
            isEditing,
            isSaving,
            handleEdit,
            handleEditCancel,
            handleEditSave,
            isTogglingEmphasis,
            handleToggleEmphasis,
            isDeleting,
            isDeleteConfirmOpen,
            executeDelete,
            handleDelete,
            handleDeleteCancel,
            handlePin,
            handleUnpin,
            isPinning,
            isPinCapReached,
            pinCapTooltip,
            canShowActionMenu,
            isThisRowSelected,
            handleRowFocus,
            handleRowBlur,
            handleRowDeselect,
            handleRowMouseDown,
        ]
    );
}
